import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Button, Chip, InputAdornment, Popover, TextField, Typography } from '@material-ui/core';
import ListAltIcon from '@material-ui/icons/ListAlt';
import SearchIcon from '@material-ui/icons/Search';
import CheckIcon from '@material-ui/icons/Check';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import NotesOutlinedIcon from '@material-ui/icons/NotesOutlined';
import clsx from 'clsx';
import { SkeletonLoader } from '../../../components/loading/SkeletonLoader';
import { CannedNote, appendCannedNote, sortForActionType } from './cannedNotes';

// Canned follow-up notes picker: a small "Canned notes" button that lives inside the Follow Up Notes field (next
// to the mic) and opens a searchable list. Picking an entry appends its text to the notes - never replaces what
// is there - and a line under the field confirms it with an Undo. The notes are listed in their sequence (set by
// dragging rows in the Canned notes lookup) and are supplied by the caller (see useCannedNotes.tsx). The notes of the follow-up action selected on the
// form are listed first.

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        pickerButton: {
            textTransform: 'none',
            fontWeight: 500,
            color: theme.palette.text.secondary,
        },
        popoverPaper: {
            width: 460,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 440,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 12,
            boxShadow: '0 8px 28px rgba(0,0,0,0.16)',
            overflow: 'hidden',
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            padding: theme.spacing(1.5, 2, 0.5),
        },
        headerIcon: {
            flex: '0 0 auto',
            width: 32,
            height: 32,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--rv-accent-blue-bg)',
            color: 'var(--rv-accent-blue)',
            '& svg': { fontSize: 18 },
        },
        headerTitle: {
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.25,
        },
        headerCount: {
            fontSize: 12,
            lineHeight: 1.25,
            color: theme.palette.text.secondary,
        },
        searchRow: {
            padding: theme.spacing(1, 2, 1.25),
            '& .MuiOutlinedInput-root': {
                borderRadius: 999,
                backgroundColor: theme.palette.action.hover,
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
                backgroundColor: theme.palette.common.white,
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--rv-accent-blue)',
                borderWidth: 1,
            },
        },
        list: {
            overflowY: 'auto',
            padding: theme.spacing(0.5, 1, 1),
            flex: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
        },
        note: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(1.5),
            width: '100%',
            textAlign: 'left',
            marginTop: theme.spacing(0.5),
            padding: theme.spacing(1.25, 1.5),
            border: 'none',
            borderRadius: 10,
            background: 'none',
            color: theme.palette.text.primary,
            cursor: 'pointer',
            font: 'inherit',
            transition: 'background-color 0.12s ease, box-shadow 0.12s ease',
        },
        // the highlighted row (the pointer or the arrow keys): a soft blue wash with an accent edge
        noteActive: {
            backgroundColor: 'var(--rv-accent-blue-bg)',
            boxShadow: 'inset 3px 0 0 var(--rv-accent-blue)',
        },
        noteIcon: {
            flex: '0 0 auto',
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': { fontSize: 17 },
        },
        noteText: {
            flex: 1,
            minWidth: 0,
        },
        noteTop: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        noteSubject: {
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.35,
        },
        noteKind: {
            marginLeft: 'auto',
            height: 18,
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
        },
        // the action types take turns at these colours (see getActionTypeColourClass)
        colour0: { backgroundColor: 'var(--rv-accent-green-bg)', color: 'var(--rv-accent-green)' },
        colour1: { backgroundColor: 'var(--rv-accent-indigo-bg)', color: 'var(--rv-accent-indigo)' },
        colour2: { backgroundColor: 'var(--rv-accent-orange-bg)', color: 'var(--rv-accent-orange)' },
        colour3: { backgroundColor: 'var(--rv-accent-blue-bg)', color: 'var(--rv-accent-blue)' },
        colour4: { backgroundColor: 'var(--rv-accent-purple-bg)', color: 'var(--rv-accent-purple)' },
        colour5: { backgroundColor: 'var(--rv-accent-teal-bg)', color: 'var(--rv-accent-teal)' },
        groupLabel: {
            padding: theme.spacing(1, 1.5, 0.25),
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
        },
        loading: {
            padding: theme.spacing(1.5),
        },
        noteBody: {
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            marginTop: 2,
            fontSize: 12.5,
            lineHeight: 1.5,
            color: theme.palette.text.secondary,
        },
        empty: {
            padding: theme.spacing(2, 1.5),
            textAlign: 'center',
            fontSize: 13,
            color: theme.palette.text.secondary,
        },
        footer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            padding: theme.spacing(1, 2),
            borderTop: `1px solid ${theme.palette.divider}`,
            fontSize: 12,
            color: theme.palette.text.secondary,
            backgroundColor: '#f8f9fc',
        },
        footerKeys: {
            flex: '0 0 auto',
            color: theme.palette.text.disabled,
        },
        inserted: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            marginTop: theme.spacing(1),
            padding: theme.spacing(0.75, 1.25),
            borderRadius: 6,
            fontSize: 12.5,
            backgroundColor: 'var(--rv-accent-green-bg)',
            color: 'var(--rv-accent-green)',
            '& svg': { fontSize: 16 },
        },
        undo: {
            marginLeft: 'auto',
            minWidth: 0,
            padding: theme.spacing(0, 0.75),
            fontSize: 12.5,
            fontWeight: 700,
            textDecoration: 'underline',
            textTransform: 'none',
            color: 'inherit',
        },
    }),
);

const ACTION_TYPE_COLOUR_COUNT = 6;

// The same action type always gets the same colour: it is picked from the action type's guid
const getActionTypeColourClass = (actionTypeGuid: string | null): string => {
    let hash = 0;
    (actionTypeGuid || '').split('').forEach((character) => {
        hash = (hash * 31 + character.charCodeAt(0)) % 997;
    });
    return `colour${hash % ACTION_TYPE_COLOUR_COUNT}`;
};

// The action types are set up by the firm, so the icon is chosen from the words in the name
const getActionTypeIcon = (actionTypeName: string): React.ReactNode => {
    const name = actionTypeName.toLowerCase();
    if (/call|phone|voicemail|spoke|text|sms/.test(name)) {
        return <PhoneOutlinedIcon />;
    }
    if (/mail|letter|send/.test(name)) {
        return <MailOutlineIcon />;
    }
    if (/meet|appointment|book|visit/.test(name)) {
        return <EventOutlinedIcon />;
    }
    return <NotesOutlinedIcon />;
};
const CONFIRMATION_MILLISECONDS = 9000;

export interface CannedNotesForFieldProps {
    notes: CannedNote[];
    isLoading?: boolean;
    // the follow-up action type selected on the form: its notes are listed first
    selectedActionTypeGuid?: string | null;
    value: string;
    onChange: (newValue: string) => void;
    // pickerNode goes inside the field (bottom-left); confirmationNode is the "Inserted" line to show under the field
    children: (nodes: { pickerNode: React.ReactNode; confirmationNode: React.ReactNode }) => React.ReactNode;
}

export const CannedNotesForField: React.FC<CannedNotesForFieldProps> = ({ notes, isLoading, selectedActionTypeGuid, value, onChange, children }) => {
    const classes = useStyles();
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
    const [searchText, setSearchText] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [lastInsert, setLastInsert] = useState<{ subject: string; previousValue: string } | null>(null);
    const confirmationTimer = useRef<number | undefined>(undefined);
    const listElementRef = useRef<HTMLDivElement | null>(null);
    // only arrow keys scroll the list: hovering a note also sets the active one, and must not make the list jump
    const shouldScrollToActiveRef = useRef(false);

    useEffect(() => () => window.clearTimeout(confirmationTimer.current), []);

    useEffect(() => {
        if (!shouldScrollToActiveRef.current || !listElementRef.current) {
            return;
        }
        shouldScrollToActiveRef.current = false;
        const activeElement = listElementRef.current.querySelectorAll('[role="option"]')[activeIndex];
        if (activeElement && activeElement.scrollIntoView) {
            activeElement.scrollIntoView({ block: 'nearest' });
        }
    }, [activeIndex]);

    const shown = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        return sortForActionType(notes, selectedActionTypeGuid).filter((note) => !query || `${note.subject} ${note.body} ${note.actionTypeName}`.toLowerCase().includes(query));
    }, [notes, searchText, selectedActionTypeGuid]);

    const open = (event: React.MouseEvent<HTMLElement>) => {
        setSearchText('');
        setActiveIndex(0);
        setAnchorElement(event.currentTarget);
    };
    const close = () => setAnchorElement(null);

    const insert = (note: CannedNote) => {
        setLastInsert({ subject: note.subject, previousValue: value });
        onChange(appendCannedNote(value, note));
        close();
        window.clearTimeout(confirmationTimer.current);
        confirmationTimer.current = window.setTimeout(() => setLastInsert(null), CONFIRMATION_MILLISECONDS);
    };

    const undo = () => {
        if (lastInsert) {
            onChange(lastInsert.previousValue);
            setLastInsert(null);
        }
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            shouldScrollToActiveRef.current = true;
            setActiveIndex((index) => Math.min(shown.length - 1, index + 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            shouldScrollToActiveRef.current = true;
            setActiveIndex((index) => Math.max(0, index - 1));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (shown[activeIndex]) {
                insert(shown[activeIndex]);
            }
        }
    };

    const renderNote = (note: CannedNote, index: number) => (
        <button
            key={note.id}
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            className={clsx(classes.note, index === activeIndex && classes.noteActive)}
            onMouseEnter={() => {
                shouldScrollToActiveRef.current = false;
                setActiveIndex(index);
            }}
            onClick={() => insert(note)}
        >
            <span className={clsx(classes.noteIcon, classes[getActionTypeColourClass(note.actionTypeGuid) as 'colour0'])}>{getActionTypeIcon(note.actionTypeName)}</span>
            <span className={classes.noteText}>
                <span className={classes.noteTop}>
                    <span className={classes.noteSubject}>{note.subject}</span>
                    {!!note.actionTypeName && (
                        <Chip size="small" label={note.actionTypeName} className={clsx(classes.noteKind, classes[getActionTypeColourClass(note.actionTypeGuid) as 'colour0'])} />
                    )}
                </span>
                <span className={classes.noteBody}>{note.body}</span>
            </span>
        </button>
    );

    const pickerNode = (
        <>
            <Button
                size="small"
                className={classes.pickerButton}
                startIcon={<ListAltIcon />}
                aria-label="Canned notes"
                aria-haspopup="listbox"
                aria-expanded={!!anchorElement}
                onClick={open}
            >
                Canned notes
            </Button>
            <Popover
                open={!!anchorElement}
                anchorEl={anchorElement}
                onClose={close}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                classes={{ paper: classes.popoverPaper }}
            >
                <div className={classes.header}>
                    <span className={classes.headerIcon}><ListAltIcon /></span>
                    <div>
                        <div className={classes.headerTitle}>Canned notes</div>
                        <div className={classes.headerCount}>{searchText.trim() ? `${shown.length} of ${notes.length} notes` : `${notes.length} notes`}</div>
                    </div>
                </div>
                <div className={classes.searchRow} onKeyDown={onKeyDown}>
                    <TextField
                        autoFocus={true}
                        fullWidth={true}
                        size="small"
                        variant="outlined"
                        placeholder="Search canned notes…"
                        value={searchText}
                        onChange={(event) => {
                            setSearchText(event.target.value);
                            setActiveIndex(0);
                        }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        inputProps={{ 'aria-label': 'Search canned notes' }}
                    />
                </div>
                <div ref={listElementRef} className={classes.list} role="listbox" aria-label="Canned notes">
                    {isLoading && notes.length === 0 && (
                        <div className={classes.loading}>
                            <SkeletonLoader variant="text" rows={4} />
                        </div>
                    )}
                    {!isLoading && notes.length === 0 && (
                        <p className={classes.empty}>No canned notes have been set up yet.</p>
                    )}
                    {notes.length > 0 && shown.length === 0 && (
                        <p className={classes.empty}>{`No canned notes match “${searchText}”.`}</p>
                    )}
                    {shown.map((note, index) => {
                        const isSelectedGroup = !!selectedActionTypeGuid && note.actionTypeGuid === selectedActionTypeGuid;
                        const previousNote = shown[index - 1];
                        const wasSelectedGroup = !!previousNote && !!selectedActionTypeGuid && previousNote.actionTypeGuid === selectedActionTypeGuid;
                        // two labels, only when the selected action has notes of its own and there are others after them
                        const label = isSelectedGroup && index === 0
                            ? `For ${note.actionTypeName}`
                            : (!isSelectedGroup && wasSelectedGroup ? 'Other notes' : null);
                        return (
                            <React.Fragment key={note.id}>
                                {!!label && <div className={classes.groupLabel}>{label}</div>}
                                {renderNote(note, index)}
                            </React.Fragment>
                        );
                    })}
                </div>
                <Typography className={classes.footer} component="div">
                    <span>Inserts the text into Follow Up Notes. You can edit it before saving.</span>
                    <span className={classes.footerKeys}>↑↓ move · Enter inserts</span>
                </Typography>
            </Popover>
        </>
    );

    const confirmationNode = lastInsert ? (
        <div className={classes.inserted} role="status">
            <CheckIcon />
            <span>{`Inserted “${lastInsert.subject}”. Edit it before saving.`}</span>
            <Button className={classes.undo} onClick={undo}>Undo</Button>
        </div>
    ) : null;

    return <>{children({ pickerNode, confirmationNode })}</>;
};
